import type { Catalogs, Translation } from "@amtana/i18n/catalog";

// Every line the milestones, invitations and live events screens show, in English and Spanish,
// read through `useGrowthCopy` (behavior/use-growth-copy.ts). The milestone, task and event texts
// come from the growth process and read through `growth-lines-copy.ts`; error copy stays in
// `growth-error-copy.ts`.

export type GrowthCopyKey = keyof typeof EN;
export type GrowthCatalog = typeof EN;

const EN = {
  titleMilestones: "Milestones · {product}",
  titleInvitations: "Invitations · {product}",
  titleLiveEvents: "Live Events · {product}",
  navLiveEvents: "Live Events",
  navMilestones: "Earn free credits",
  navRefer: "Refer & earn",
  crumbHome: "Home",
  crumbMilestones: "Milestones",
  crumbReferFriend: "Refer a friend",
  crumbLiveEvents: "Live Events",

  // Getting started.
  welcomeVideoTitle: "Welcome to {product}!",
  welcomeVideoDuration: "4 min 49 sec",
  claudeBadge: "New",
  claudeEyebrow: "Claude integration",
  claudeTitle: "Use {product} right from Claude",
  claudeDescription:
    "Connect your account through MCP and ask Claude to analyze accounts, filter prospects and prepare campaigns, without leaving the chat.",
  claudeQuote: "“Analyze the followers of @account and keep only the ones with an email”",
  claudeAction: "Connect with Claude",
  claudeToastTitle: "Claude connection",
  claudeToastDescription: "Connecting Claude isn't available in this simulation.",
  demoTitle: "Book a demo to get most out of {product}",
  demoDescription:
    "See how to kickstart {product} successfully, achieve top results fast & get the value for money.",
  demoAction: "Book a demo",
  demoToastDescription: "Opens the booking page in the real product.",
  successManager: "Success Manager",

  // Earn free credits.
  earnTitle: "Earn Free Credits!",
  earnDescription: {
    one: "Complete tasks and earn up to 1 credit",
    other: "Complete tasks and earn up to {count} credits",
  },
  earned: "Earned",
  complete: "Complete",
  milestonesTitle: "Earn Free Credits",
  milestonesDescription:
    "Complete tasks and claim free credits as a reward. The more you do, the more you earn!",
  taskCredits: { one: "+{count} credit", other: "+{count} credits" },
  taskCompleted: "{title} completed.",
  couldNotCompleteTask: "Could not complete the task",
  gettingStarted: "Getting Started",
  tasksCompleted: "{done} of {total} tasks completed",
  creditsEarnedLabel: "Credits earned",
  creditsEarnedOf: "{earned} / {available} credits earned",
  howLink: "How?",
  claimed: "Claimed",
  claim: "Claim",
  claimedToast: { one: "+{count} credit claimed", other: "+{count} credits claimed" },
  couldNotClaim: "Could not claim the credits",

  // Invitations.
  referTitle: "Get Free Credits by Referring Friends!",
  referDescription: {
    one: "Once a friend you referred verifies their account, you both receive 1 credit.",
    other: "Once a friend you referred verifies their account, you both receive {count} credits.",
  },
  yourReferralLink: "Your referral link",
  loadingLink: "Loading your link…",
  copyLink: "Copy link",
  linkCopied: "Referral link copied",
  goodUntil: "Good until {date}.",
  yourRewards: "Your rewards",
  friendsReferred: {
    one: "{count} friend referred and verified.",
    other: "{count} friends referred and verified.",
  },
  earnedFromReferrals: {
    one: "You've earned {count} credit from referrals.",
    other: "You've earned {count} credits from referrals.",
  },

  // Live events.
  liveEventsTitle: "Live Events",
  liveEventsDescription: "Learn with the team, live.",
  noEvents: "No events scheduled",
  registrationCancelled: "Registration cancelled",
  registeredToast: "You're registered. We'll email you the link",
  couldNotChangeRegistration: "Could not change your registration",
  registeredButton: "Registered ✓",
  register: "Register",
} as const;

const ES: Translation<GrowthCatalog> = {
  titleMilestones: "Hitos · {product}",
  titleInvitations: "Invitaciones · {product}",
  titleLiveEvents: "Eventos en vivo · {product}",
  navLiveEvents: "Eventos en vivo",
  navMilestones: "Gana créditos gratis",
  navRefer: "Invita y gana",
  crumbHome: "Inicio",
  crumbMilestones: "Hitos",
  crumbReferFriend: "Invita a un amigo",
  crumbLiveEvents: "Eventos en vivo",

  welcomeVideoTitle: "¡Te damos la bienvenida a {product}!",
  welcomeVideoDuration: "4 min 49 s",
  claudeBadge: "Nuevo",
  claudeEyebrow: "Integración con Claude",
  claudeTitle: "Usa {product} desde Claude",
  claudeDescription:
    "Conecta tu cuenta con MCP y pídele a Claude que analice cuentas, filtre prospectos y prepare campañas, sin salir del chat.",
  claudeQuote: "“Analiza los seguidores de @account y quédate solo con los que tengan correo”",
  claudeAction: "Conectar con Claude",
  claudeToastTitle: "Conexión con Claude",
  claudeToastDescription: "Conectar Claude no está disponible en esta simulación.",
  demoTitle: "Reserva una demo para sacar el máximo partido a {product}",
  demoDescription:
    "Mira cómo empezar con buen pie en {product}, lograr grandes resultados rápido y aprovechar al máximo tu inversión.",
  demoAction: "Reservar una demo",
  demoToastDescription: "Abre la página de reservas en el producto real.",
  successManager: "Gerente de éxito",

  earnTitle: "¡Gana créditos gratis!",
  earnDescription: {
    one: "Completa tareas y gana hasta 1 crédito",
    other: "Completa tareas y gana hasta {count} créditos",
  },
  earned: "Ganados",
  complete: "Completar",
  milestonesTitle: "Gana créditos gratis",
  milestonesDescription:
    "Completa tareas y reclama créditos gratis como recompensa. ¡Cuanto más hagas, más ganas!",
  taskCredits: { one: "+{count} crédito", other: "+{count} créditos" },
  taskCompleted: "{title}: completada.",
  couldNotCompleteTask: "No pudimos completar la tarea",
  gettingStarted: "Primeros pasos",
  tasksCompleted: "{done} de {total} tareas completadas",
  creditsEarnedLabel: "Créditos ganados",
  creditsEarnedOf: "{earned} / {available} créditos ganados",
  howLink: "¿Cómo?",
  claimed: "Reclamado",
  claim: "Reclamar",
  claimedToast: { one: "+{count} crédito reclamado", other: "+{count} créditos reclamados" },
  couldNotClaim: "No pudimos reclamar los créditos",

  referTitle: "¡Gana créditos gratis invitando a tus amigos!",
  referDescription: {
    one: "Cuando un amigo que invitaste verifique su cuenta, los dos reciben 1 crédito.",
    other: "Cuando un amigo que invitaste verifique su cuenta, los dos reciben {count} créditos.",
  },
  yourReferralLink: "Tu enlace de invitación",
  loadingLink: "Cargando tu enlace…",
  copyLink: "Copiar enlace",
  linkCopied: "Enlace de invitación copiado",
  goodUntil: "Válido hasta el {date}.",
  yourRewards: "Tus recompensas",
  friendsReferred: {
    one: "{count} amigo invitado y verificado.",
    other: "{count} amigos invitados y verificados.",
  },
  earnedFromReferrals: {
    one: "Has ganado {count} crédito por invitaciones.",
    other: "Has ganado {count} créditos por invitaciones.",
  },

  liveEventsTitle: "Eventos en vivo",
  liveEventsDescription: "Aprende en vivo con el equipo.",
  noEvents: "No hay eventos programados",
  registrationCancelled: "Registro cancelado",
  registeredToast: "Ya estás registrado. Te enviaremos el enlace por correo",
  couldNotChangeRegistration: "No pudimos cambiar tu registro",
  registeredButton: "Registrado ✓",
  register: "Registrarme",
};

export const GROWTH_CATALOGS: Catalogs<GrowthCatalog> = { en: EN, es: ES };
